import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { bulkCreateCreditMemos } from '../../config/api';
import { useQuery, useMutation } from '@tanstack/react-query';
import { ArrowLeft, FileText, Calendar, AlertCircle, CheckCircle, Clock, XCircle, Database, TrendingUp, Download, ChevronDown, ChevronRight, Layers } from 'lucide-react';
import { getBatchDetail, generateFinanceExport, getFinanceExportHistory, downloadFinanceExport } from '../../config/api';
import { toast } from 'react-hot-toast';

const money = (v) => `$${(parseFloat(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Soft, consistent card shell with a subtle lift on hover.
const Card = ({ children, className = '' }) => (
  <div
    className={`rounded-2xl border border-gray-100 bg-white shadow-card transition-all duration-200 hover:-translate-y-0.5 hover:shadow-lg ${className}`}
  >
    {children}
  </div>
);

const BatchDetail = () => {
  const { id } = useParams();
  const [generatingMemos, setGeneratingMemos] = React.useState(false);
  const handleGenerateMemos = async () => {
    if (!window.confirm('Generate credit memos for all eligible claims in this batch (Validated/Approved/Posted, no existing memo)?')) return;
    setGeneratingMemos(true);
    try {
      const res = await bulkCreateCreditMemos({ batchId: id });
      const d = res?.data || res;
      toast.success(`Generated ${d.createdCount} credit memo(s)${d.skippedCount ? `, skipped ${d.skippedCount}` : ''}`);
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to generate credit memos');
    } finally { setGeneratingMemos(false); }
  };
  const navigate = useNavigate();
  const [isGenerating, setIsGenerating] = useState(false);
  const [expanded, setExpanded] = useState({});

  const { data: batch, isLoading, error } = useQuery({
    queryKey: ['batch', id],
    queryFn: () => getBatchDetail(id),
    enabled: !!id,
    retry: 1
  });

  const { data: exportHistory } = useQuery({
    queryKey: ['financeExports', id],
    queryFn: () => getFinanceExportHistory(id),
    enabled: !!id && !!batch && ['POSTED', 'READY_TO_POST', 'COMPLETED'].includes(batch.status),
    retry: 1
  });

  const generateExportMutation = useMutation({
    mutationFn: ({ batchId, format }) => generateFinanceExport(batchId, format),
    onSuccess: (data) => {
      toast.success('Finance export generated successfully');
      handleDownloadExport(data.data.exportId, data.data.fileName);
    },
    onError: (err) => {
      toast.error('Failed to generate finance export');
      console.error('Export error:', err);
    },
    onSettled: () => setIsGenerating(false)
  });

  const handleGenerateExport = (format) => {
    if (!batch) return;
    setIsGenerating(true);
    generateExportMutation.mutate({ batchId: batch.id, format });
  };

  const handleDownloadExport = async (exportId, fileName) => {
    try {
      const blob = await downloadFinanceExport(exportId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download export file');
      console.error('Download error:', err);
    }
  };

  const getStatusInfo = (status) => {
    const statusConfig = {
      IMPORTED: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Imported' },
      CREATED: { color: 'bg-blue-100 text-blue-800', icon: Clock, label: 'Created' },
      VALIDATING: { color: 'bg-yellow-100 text-yellow-800', icon: Clock, label: 'Validating' },
      VALIDATED: { color: 'bg-teal-100 text-teal-800', icon: CheckCircle, label: 'Validated' },
      PROCESSING: { color: 'bg-purple-100 text-purple-800', icon: TrendingUp, label: 'Processing' },
      NEEDS_REVIEW: { color: 'bg-orange-100 text-orange-800', icon: AlertCircle, label: 'Needs Review' },
      READY_TO_POST: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Ready to Post' },
      APPROVED: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Approved' },
      REJECTED: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Rejected' },
      POSTED: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Posted' },
      COMPLETED: { color: 'bg-green-100 text-green-800', icon: CheckCircle, label: 'Completed' },
      FAILED: { color: 'bg-red-100 text-red-800', icon: XCircle, label: 'Failed' }
    };
    return statusConfig[status] || { color: 'bg-gray-100 text-gray-800', icon: Clock, label: status };
  };

  const Header = () => (
    <div className="flex items-center gap-4 mb-6">
      <button
        onClick={() => navigate('/app/batches')}
        title="Back to batches"
        className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
      >
        <ArrowLeft size={20} />
      </button>
      <h1 className="text-2xl font-bold text-gray-900">Batch Details</h1>
    </div>
  );

  if (isLoading) {
    return (
      <div className="p-6">
        <Header />
        <Card className="p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading batch details...</p>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6">
        <Header />
        <Card className="p-8 text-center">
          <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Error Loading Batch</h2>
          <p className="text-gray-500">Failed to load batch details. Please try again.</p>
        </Card>
      </div>
    );
  }

  if (!batch) {
    return (
      <div className="p-6">
        <Header />
        <Card className="p-8 text-center">
          <h2 className="text-xl font-semibold text-gray-700 mb-2">Batch Not Found</h2>
          <p className="text-gray-500">The requested batch could not be found.</p>
        </Card>
      </div>
    );
  }

  const statusInfo = getStatusInfo(batch.status);
  const StatusIcon = statusInfo.icon;
  const claims = batch.claims || [];
  const canExport = ['POSTED', 'READY_TO_POST', 'COMPLETED'].includes(batch.status);
  const toggle = (claimId) => setExpanded((s) => ({ ...s, [claimId]: !s[claimId] }));

  const stat = [
    { label: 'Claims', value: batch._count?.claims ?? claims.length, accent: 'text-primary-700' },
    { label: 'Line Items', value: batch.lineCount ?? 0, accent: 'text-gray-900' },
    { label: 'Errors', value: batch.errorLineCount ?? batch.errorCount ?? 0, accent: (batch.errorLineCount || batch.errorCount) ? 'text-red-600' : 'text-gray-900' },
    { label: 'Total Amount', value: money(batch.totalAmount), accent: 'text-accent-600' }
  ];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/app/batches')}
            title="Back to batches"
            className="p-2 text-gray-600 hover:text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-2xl font-bold text-gray-900">Batch Details</h1>
        </div>
        <button
          onClick={() => navigate(`/app/batches/${id}/line-items`)}
          title="View line items"
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 shadow-sm transition-all hover:shadow"
        >
          <FileText size={16} />
          Line Items Editor
        </button>
        <button
          onClick={handleGenerateMemos}
          disabled={generatingMemos}
          title="Generate credit memos for eligible claims in this batch"
          className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 shadow-sm transition-all hover:shadow disabled:opacity-60 ml-3"
        >
          <FileText size={16} />
          {generatingMemos ? 'Generating…' : 'Generate Credit Memos'}
        </button>
      </div>

      {/* Header card with stat tiles */}
      <Card className="p-6 mb-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">{batch.batchNumber}</h2>
            {batch.description && <p className="text-gray-500 mt-1">{batch.description}</p>}
          </div>
          <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusInfo.color}`}>
            <StatusIcon className="w-4 h-4 mr-2" />
            {statusInfo.label}
          </span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stat.map((s) => (
            <div key={s.label} className="rounded-xl bg-gray-50/70 p-4 text-center transition-colors hover:bg-gray-100/70">
              <div className={`text-2xl font-bold ${s.accent}`}>{s.value}</div>
              <div className="text-sm text-gray-500 mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Claim-status roll-up */}
        {batch.statusBreakdown && Object.keys(batch.statusBreakdown).length > 0 && (
          <div className="flex flex-wrap gap-2 mt-5 pt-5 border-t border-gray-100">
            {Object.entries(batch.statusBreakdown).map(([st, count]) => {
              const si = getStatusInfo(st);
              return (
                <span key={st} className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${si.color}`}>
                  {si.label}: {count}
                </span>
              );
            })}
          </div>
        )}
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Claims list */}
        <div className="lg:col-span-2 space-y-4">
          <div className="flex items-center gap-2 text-gray-900">
            <Layers className="w-5 h-5 text-primary-600" />
            <h3 className="text-lg font-semibold">Claims ({claims.length})</h3>
          </div>

          {claims.length === 0 && (
            <Card className="p-8 text-center text-gray-500">No claims in this batch.</Card>
          )}

          {claims.map((claim) => {
            const cs = getStatusInfo(claim.status);
            const CsIcon = cs.icon;
            const isOpen = !!expanded[claim.id];
            const claimErrors = claim.validationErrors || [];
            const hasErrors = claimErrors.length > 0 || (claim.lines || []).some((l) => (l.validationErrors || []).length > 0 || l.isWithinTolerance === false);
            return (
              <Card key={claim.id} className="overflow-hidden">
                <button
                  onClick={() => toggle(claim.id)}
                  className="w-full flex items-center justify-between gap-3 p-4 text-left hover:bg-gray-50/60 transition-colors"
                >
                  <div className="flex items-center gap-3 min-w-0">
                    {isOpen ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" /> : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />}
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 truncate">{claim.claimNumber}</span>
                        {hasErrors && <AlertCircle className="w-4 h-4 text-red-500 shrink-0" title="Has validation issues" />}
                      </div>
                      <div className="text-xs text-gray-500 truncate">
                        {claim.customer?.name || 'Unknown customer'}
                        {claim.customer?.state ? ` · ${claim.customer.state}` : ''}
                        {claim.contract?.number ? ` · Contract ${claim.contract.number}` : ''}
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <span className="text-sm font-semibold text-gray-900">{money(claim.totalAmount)}</span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${cs.color}`}>
                      <CsIcon className="w-3.5 h-3.5 mr-1" />
                      {cs.label}
                    </span>
                  </div>
                </button>

                {isOpen && (
                  <div className="border-t border-gray-100 p-4 space-y-4 bg-gray-50/40">
                    {/* Claim-level meta */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                      <div><div className="text-xs text-gray-500">Claim Date</div><div className="text-gray-900">{claim.claimDate ? new Date(claim.claimDate).toLocaleDateString() : '—'}</div></div>
                      <div><div className="text-xs text-gray-500">Validation</div><div className="text-gray-900">{claim.validationStatus || '—'}</div></div>
                      <div><div className="text-xs text-gray-500">Purpose</div><div className="text-gray-900">{claim.purposeCode || '—'}{claim.isResubmission ? ' (resubmission)' : ''}</div></div>
                      <div><div className="text-xs text-gray-500">EDI Txn</div><div className="text-gray-900 truncate" title={claim.ediTransactionId || ''}>{claim.ediTransactionId || '—'}</div></div>
                    </div>

                    {/* Claim-level errors / rejection */}
                    {(claimErrors.length > 0 || claim.errorReason || claim.rejectionReasonDetail) && (
                      <div className="rounded-lg border border-red-100 bg-red-50 p-3">
                        <div className="text-xs font-semibold text-red-700 mb-1 flex items-center gap-1"><AlertCircle className="w-3.5 h-3.5" /> Validation / Rejection</div>
                        {claim.rejectionReasonDetail && (
                          <div className="text-sm text-red-800">{claim.rejectionReasonDetail.code} — {claim.rejectionReasonDetail.description}</div>
                        )}
                        {claim.errorReason && <div className="text-sm text-red-800">{claim.errorReason}</div>}
                        {claimErrors.map((e, i) => <div key={i} className="text-sm text-red-800">• {e}</div>)}
                      </div>
                    )}

                    {/* Line items */}
                    <div className="overflow-x-auto rounded-lg border border-gray-100 bg-white">
                      <table className="min-w-full text-sm">
                        <thead className="bg-gray-50 text-gray-500">
                          <tr>
                            <th className="px-3 py-2 text-left font-medium">#</th>
                            <th className="px-3 py-2 text-left font-medium">Product</th>
                            <th className="px-3 py-2 text-left font-medium">NDC</th>
                            <th className="px-3 py-2 text-right font-medium">Qty</th>
                            <th className="px-3 py-2 text-right font-medium">Sales Price</th>
                            <th className="px-3 py-2 text-right font-medium">Contract</th>
                            <th className="px-3 py-2 text-right font-medium">Variance</th>
                            <th className="px-3 py-2 text-left font-medium">Status</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {(claim.lines || []).map((line) => {
                            const lineErrors = line.validationErrors || [];
                            const bad = lineErrors.length > 0 || line.isWithinTolerance === false;
                            return (
                              <React.Fragment key={line.id}>
                                <tr className={bad ? 'bg-red-50/40' : ''}>
                                  <td className="px-3 py-2 text-gray-500">{line.lineNo}</td>
                                  <td className="px-3 py-2 text-gray-900">{line.product?.name || line.productName || '—'}</td>
                                  <td className="px-3 py-2 text-gray-600">{line.product?.ndc11 || line.productNdc || '—'}</td>
                                  <td className="px-3 py-2 text-right text-gray-900">{line.quantity}</td>
                                  <td className="px-3 py-2 text-right text-gray-900">{money(line.salesPrice)}</td>
                                  <td className="px-3 py-2 text-right text-gray-600">{line.contractPrice != null ? money(line.contractPrice) : '—'}</td>
                                  <td className={`px-3 py-2 text-right ${line.variance != null && parseFloat(line.variance) !== 0 ? 'text-red-600' : 'text-gray-600'}`}>{line.variance != null ? money(line.variance) : '—'}</td>
                                  <td className="px-3 py-2">
                                    <span className={`inline-flex items-center gap-1 text-xs font-medium ${bad ? 'text-red-600' : 'text-green-600'}`}>
                                      {bad ? <AlertCircle className="w-3.5 h-3.5" /> : <CheckCircle className="w-3.5 h-3.5" />}
                                      {line.validationStatus || (bad ? 'Error' : 'OK')}
                                    </span>
                                  </td>
                                </tr>
                                {bad && (lineErrors.length > 0 || line.errorReason) && (
                                  <tr className="bg-red-50/40">
                                    <td></td>
                                    <td colSpan={7} className="px-3 pb-2 text-xs text-red-700">
                                      {line.errorReason && <div>• {line.errorReason}</div>}
                                      {lineErrors.map((e, i) => <div key={i}>• {e}</div>)}
                                    </td>
                                  </tr>
                                )}
                              </React.Fragment>
                            );
                          })}
                          {(claim.lines || []).length === 0 && (
                            <tr><td colSpan={8} className="px-3 py-4 text-center text-gray-400">No line items.</td></tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        {/* Sidebar */}
        <div className="space-y-6">
          {batch.file && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-primary-600" />
                Source File
              </h3>
              <div className="space-y-3 text-sm">
                <div><div className="text-xs text-gray-500">Filename</div><div className="text-gray-900 break-all">{batch.file.filename}</div></div>
                <div><div className="text-xs text-gray-500">Type</div><div className="text-gray-900">{batch.file.fileType}</div></div>
              </div>
            </Card>
          )}

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Calendar className="w-5 h-5 mr-2 text-primary-600" />
              Timeline
            </h3>
            <div className="space-y-4 text-sm">
              <div><div className="text-xs text-gray-500">Created</div><div className="text-gray-900">{new Date(batch.createdAt).toLocaleString()}</div></div>
              {batch.processedAt && <div><div className="text-xs text-gray-500">Processed</div><div className="text-gray-900">{new Date(batch.processedAt).toLocaleString()}</div></div>}
              {batch.updatedAt && <div><div className="text-xs text-gray-500">Last Updated</div><div className="text-gray-900">{new Date(batch.updatedAt).toLocaleString()}</div></div>}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
              <Database className="w-5 h-5 mr-2 text-primary-600" />
              Summary
            </h3>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-600">Status</span><span className="font-medium text-gray-900">{statusInfo.label}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Claims</span><span className="font-medium text-gray-900">{batch._count?.claims ?? claims.length}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Line Items</span><span className="font-medium text-gray-900">{batch.lineCount ?? 0}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Lines w/ Errors</span><span className="font-medium text-gray-900">{batch.errorLineCount ?? 0}</span></div>
              <div className="flex justify-between"><span className="text-gray-600">Total Amount</span><span className="font-medium text-gray-900">{money(batch.totalAmount)}</span></div>
            </div>
          </Card>

          {canExport && (
            <Card className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <FileText className="w-5 h-5 mr-2 text-accent-600" />
                Finance Export
              </h3>
              <p className="text-sm text-gray-600 mb-4">
                Generate a finance export file with calculated rebate amounts for check processing.
              </p>
              <div className="flex flex-wrap gap-3">
                {['csv', 'xlsx', 'txt'].map((fmt) => (
                  <button
                    key={fmt}
                    onClick={() => handleGenerateExport(fmt)}
                    disabled={isGenerating}
                    title={`Export ${fmt.toUpperCase()}`}
                    className="flex items-center px-4 py-2 bg-accent-600 text-white rounded-lg hover:bg-accent-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    {isGenerating ? '…' : fmt.toUpperCase()}
                  </button>
                ))}
              </div>

              {exportHistory?.data && exportHistory.data.length > 0 && (
                <div className="mt-6">
                  <h4 className="text-md font-medium text-gray-900 mb-3">Export History</h4>
                  <div className="space-y-2">
                    {exportHistory.data.map((exportItem) => (
                      <div key={exportItem.id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                        <div className="flex items-center space-x-3 min-w-0">
                          <FileText className="w-4 h-4 text-gray-500 shrink-0" />
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{exportItem.fileName}</p>
                            <p className="text-xs text-gray-500">
                              {exportItem.recordCount} records • {money(exportItem.totalAmount)} • {new Date(exportItem.exportedAt).toLocaleString()}
                            </p>
                          </div>
                        </div>
                        <button
                          onClick={() => handleDownloadExport(exportItem.id, exportItem.fileName)}
                          title="Download export"
                          className="flex items-center px-3 py-1 text-sm text-primary-600 hover:text-primary-800 shrink-0"
                        >
                          <Download className="w-4 h-4 mr-1" />
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatchDetail;
